import constants from 'app/shared/constants/ConstantsBundle';
import assign from 'lodash/assign';
import isPlainObject from 'lodash/isPlainObject';
import reduxUtils from 'app/shared/utils/reduxUtils';
import mapConstants from 'app/shared/constants/MapConstants';
import { getCurrentEnvForClientSide, PRODUCTION } from '@zg-rentals/rental-platform-config';
import { getNamespace } from '@zg-rentals/environment-utils';

interface Notification {
  visible: boolean;
  visibleTime?: number;
  content: string | null;
  notificationStyle: 'primary' | 'alert' | 'accent';
}

export interface Device {
  deviceType: string;
  customVariableSiteType: string;
  isMobile: boolean;
  screenWidth: string;
}

interface Bounds {
  maxLat: number | null;
  minLat: number | null;
  maxLon: number | null;
  minLon: number | null;
}

interface Overlays {
  photoGalleryFull: boolean;
  bicycleOverlay: boolean;
  transitOverlay: boolean;
  createSearchAlert: boolean;
  optInSearchAlerts: boolean;
  mapOverlay: string;
}

interface PopupModal {
  visible: boolean;
  component: string | null;
  data: Record<string, unknown>;
}

export interface AppState {
  buildNumber: number;
  advancedFilterVisible: boolean;
  clientLoaded: boolean;
  clusterSettingDict: Record<string, string | number>;
  device: Device;
  fetchListingsByCoordsComplete: boolean;
  fetchListingsByCoordsOnMapIdleComplete: boolean;
  fetchNearbyListingsComplete: boolean;
  gmapApiReady: boolean;
  gmapLoaded: boolean;
  signInWithGoogleInit: boolean;
  hamburger: boolean;
  host: string | null;
  indexAdWrapperInit: boolean;
  ip: string;
  fullReqIps: Record<string, unknown>;
  ipFromJava: Record<string, unknown>;
  isClientSideLoadedPage: boolean;
  isInitialSsrPage: boolean;
  isMapPanning: boolean;
  isOnline: boolean;
  isValidAreaPage: boolean;
  isValidSearchSlug: boolean;
  loadStatePageComplete: boolean;
  mapBounds: Bounds;
  mapShouldPan: boolean;
  overlays: Overlays;
  mapType: string;
  popupModalOpen: boolean;
  popupModal: PopupModal;
  preserveAreaBoundary: boolean;
  reviewsPopupModalOpen: boolean;
  server: string | null;
  showFilterModal: boolean;
  showNotification: Notification;
  hasShownPostContactSavedSearch: boolean;
  twitterInit: boolean;
  userAgent: string | null;
  isChatGPT?: boolean;
  userExists: boolean;
  xEids: Array<string>;
  zgEnv: string;
  isProd: boolean;
  traceId: string;
  requestId: string;
  bbTestGroup: string | null;
  advertisementEnv: string;
  clickstreamApi: string;
  clickstreamApiKey: string;
  clickstreamWriteKey: string;
  googleMaps?: {
    key: string;
  };
}

const initState = (): AppState => ({
  buildNumber: __BUILD_NUMBER__,
  advancedFilterVisible: false,
  clientLoaded: false,
  clusterSettingDict: {},
  device: {
    deviceType: 'mobile',
    customVariableSiteType: 'mobileWeb',
    isMobile: true,
    screenWidth: 'sm',
  },
  fetchListingsByCoordsComplete: false,
  fetchListingsByCoordsOnMapIdleComplete: false,
  fetchNearbyListingsComplete: false,
  gmapApiReady: false, // google map script initialized
  gmapLoaded: false, // our google map instance loaded (available in window)
  signInWithGoogleInit: false,
  hamburger: false,
  host: null,
  indexAdWrapperInit: false,
  ip: '',
  fullReqIps: {},
  ipFromJava: {},
  isClientSideLoadedPage: false,
  isInitialSsrPage: true,
  isMapPanning: false,
  isOnline: true,
  isValidAreaPage: true,
  isValidSearchSlug: true,
  loadStatePageComplete: false,
  mapBounds: {
    maxLat: null,
    minLat: null,
    maxLon: null,
    minLon: null,
  },
  mapShouldPan: false,
  overlays: {
    photoGalleryFull: false,
    bicycleOverlay: false,
    transitOverlay: false,
    createSearchAlert: false,
    optInSearchAlerts: false,
    mapOverlay: mapConstants.NO_OVERLAY,
  },
  mapType: mapConstants.DEFAULT,
  popupModalOpen: false,
  popupModal: {
    visible: false,
    component: null,
    data: {},
  },
  preserveAreaBoundary: false,
  reviewsPopupModalOpen: false,
  server: null,
  showFilterModal: false,
  showNotification: {
    visible: false,
    content: null,
    notificationStyle: 'primary',
  },
  hasShownPostContactSavedSearch: false,
  twitterInit: false,
  userAgent: null,
  isChatGPT: false,
  userExists: false,
  xEids: [],
  zgEnv: '',
  // isomorphic prod check
  isProd: typeof window === 'undefined' ? getNamespace() === 'production' : getCurrentEnvForClientSide() === PRODUCTION,
  traceId: '',
  requestId: '',
  bbTestGroup: null,
  advertisementEnv: '',
  clickstreamApi: '',
  clickstreamApiKey: '',
  clickstreamWriteKey: '',
  googleMaps: {} as { key: string },
});

const mapActionsToReducer = {
  [constants.SERVER_SIDE_APP_STORE_INIT_STATE]: (
    state: AppState,
    action: {
      payload: {
        advertisementEnv: string;
        clickstreamApi: string;
        clickstreamApiKey: string;
        clickstreamWriteKey: string;
        googleMaps: { key: string };
        isMobile: boolean;
        isChatGPT?: boolean;
        zgEnv: string;
        userAgent: string;
        host: string | null;
        server: string | null;
        ip: string;
        clusterSettingDict: Record<string, string | number>;
        requestId: string;
        traceId: string;
      };
    },
  ) => {
    const initData = action.payload;

    if (!isPlainObject(initData)) {
      return state;
    }
    let device: Device;
    if (initData.isMobile) {
      device = {
        deviceType: 'mobile',
        customVariableSiteType: 'mobileWeb',
        isMobile: true,
        screenWidth: 'sm',
      };
    } else {
      device = {
        deviceType: 'desktop',
        customVariableSiteType: 'desktopWeb',
        isMobile: false,
        screenWidth: 'xl',
      };
    }
    return assign({}, state, {
      advertisementEnv: initData.advertisementEnv,
      clickstreamApi: initData.clickstreamApi,
      clickstreamApiKey: initData.clickstreamApiKey,
      clickstreamWriteKey: initData.clickstreamWriteKey,
      googleMaps: initData.googleMaps,
      device,
      zgEnv: initData.zgEnv,
      userAgent: initData.userAgent,
      isChatGPT: initData.isChatGPT || false,
      host: initData.host || state.host,
      server: initData.server || state.server,
      ip: initData.ip || state.ip,
      clusterSettingDict: initData.clusterSettingDict || state.clusterSettingDict,
      requestId: initData.requestId,
      traceId: initData.traceId,
    });
  },
  [constants.UPDATE_CLUSTER_SETTINGS]: (state: AppState, action: { payload: Record<string, string | number> }) => {
    return assign({}, state, {
      clusterSettingDict: action.payload,
    });
  },
  [constants.NOTIFICATION_DISPLAY_BOOL]: (
    state: AppState,
    action: {
      payload: {
        visible: boolean;
        visibleTime: number;
        content: string | null;
        notificationStyle: 'primary' | 'alert' | 'accent';
      };
    },
  ) => {
    let newState = {
      visible: false,
      visibleTime: 0,
      content: null,
    } as Notification;

    if (!action.payload) {
      // Use default state
    } else if (action.payload.content) {
      // Allow users to trigger the notification modal without passing in a visible property.
      newState = {
        visible: true,
        content: action.payload.content,
        notificationStyle: action.payload.notificationStyle,
      };
    } else {
      newState = {
        visible: action.payload.visible,
        content: action.payload.content,
        notificationStyle: action.payload.notificationStyle,
      };
    }

    if (action.payload && action.payload.visibleTime) {
      newState.visibleTime = action.payload.visibleTime;
    }

    // TODO: Add way to pass in custom css themes, so we can display alert message on map instead of just at
    // top of the page.
    return assign({}, state, {
      showNotification: assign({}, state.showNotification, newState),
    });
  },
  [constants.SHOW_POPUP_MODAL]: (
    state: AppState,
    action: {
      payload: {
        component: unknown;
        data: unknown;
      };
    },
  ) => {
    if (!action.payload) {
      return state;
    }
    return assign({}, state, {
      popupModalOpen: true,
      popupModal: assign({}, state.popupModal, {
        visible: true,
        component: action.payload.component || {},
        data: action.payload.data || {},
      }),
    });
  },
  [constants.HIDE_POPUP_MODAL]: (state: AppState) => {
    return assign({}, state, {
      popupModalOpen: false,
      popupModal: assign({}, initState().popupModal),
    });
  },
  [constants.GMAP_API_READY]: (state: AppState) => {
    return assign({}, state, {
      gmapApiReady: true,
    });
  },
  [constants.TWITTER_INIT_SUCCESS]: (state: AppState) => {
    return assign({}, state, {
      twitterInit: true,
    });
  },
  [constants.SIGN_IN_WITH_GOOGLE_INIT_SUCCESS]: (state: AppState) => {
    return assign({}, state, {
      signInWithGoogleInit: true,
    });
  },
  [constants.TOGGLE_OVERLAY]: (state: AppState, action: { payload: { type: keyof Overlays; bool: boolean } }) => {
    const partialStateObject = { ...state.overlays };

    (partialStateObject[action.payload.type] as boolean) = action.payload.bool;

    return {
      ...state,
      overlays: partialStateObject,
    };
  },
  [constants.MAP_OVERLAY_CHANGED]: (state: AppState, action: { payload: string }) => {
    const overlays = { ...state.overlays };
    const transitOverlayKey = action.payload;
    return {
      ...state,
      overlays: {
        ...overlays,
        mapOverlay: transitOverlayKey,
      },
    };
  },
  [constants.MAP_TYPE_CHANGED]: (state: AppState, action: { payload: string }) => {
    const mapType = action.payload;

    return {
      ...state,
      mapType,
    };
  },
  [constants.SET_APP_STORE_BOOL]: (state: AppState, action: { payload: { name: string; bool: boolean } }) => {
    const partialStateObject = {};

    // @ts-expect-error Need to fix action types
    partialStateObject[action.payload.name] = action.payload.bool;
    return assign({}, state, partialStateObject);
  },
  [constants.SET_BOUNTY_BRAIN_TEST_GROUP]: (state: AppState, action: { payload: { bbTestGroup: string } }) => {
    return assign({}, state, { bbTestGroup: action.payload.bbTestGroup });
  },
  [constants.HAMBURGER_ACTIVE]: (state: AppState) => {
    return assign({}, state, {
      hamburger: true,
    });
  },
  [constants.HAMBURGER_INACTIVE]: (state: AppState) => {
    return assign({}, state, {
      hamburger: false,
    });
  },
  [constants.PUSH_XEID]: (state: AppState, action: { payload: string }) => {
    const newxEid = action.payload;

    return assign({}, state, {
      // @ts-ignore Not sure how to type this at the moment
      xEids: [].concat(state.xEids).concat(newxEid),
    });
  },
  [constants.SET_DEVICE]: (state: AppState, action: { payload: Device }) => {
    return assign({}, state, {
      device: action.payload,
    });
  },
  [constants.UPDATE_IP_FROM_JAVA]: (state: AppState, action: { payload: Record<string, unknown> }) => {
    return assign({}, state, {
      ipFromJava: action.payload,
    });
  },
};

const app = reduxUtils.createReducer(mapActionsToReducer, initState());

export default app;
